import { TABLE_TALK } from '@tira/protocol';
import { useEffect, useReducer, useState, type CSSProperties, type JSX } from 'react';
import { COPY } from '../i18n/copy.js';
import { useT, type CopyKey, type T } from '../i18n/index.js';
import { useApp } from '../state/store.js';
import './TableTalk.css';

/**
 * Table talk is twelve presets and nothing else — no free text at v1, which is
 * what keeps an 18+ table moderatable without a moderation team (§11).
 */

const TALK_TEXT = new Map<string, string>(TABLE_TALK.map((t) => [t.id, t.text] as [string, string]));

/** How long a thrown preset stays on the table before it clears. */
const LIFETIME_MS = 3_000;

/**
 * A preset travels as an id, so both the strip and the float say it in the
 * player's language. The protocol's own Taglish is the fallback, for an id this
 * build has never been told about.
 */
function talkText(t: T, preset: string): string {
  const key = `ui.talk.preset.${preset}`;
  return key in COPY ? t(key as CopyKey) : (TALK_TEXT.get(preset) ?? preset);
}

export interface TableTalkProps {
  /** Fires with the preset id; the caller sends it to the server. */
  onSend(preset: string): void;
  /** The player muted table talk in responsible-play settings (S15). */
  muted?: boolean;
  disabled?: boolean;
  className?: string;
}

export function TableTalk({ onSend, muted = false, disabled = false, className }: TableTalkProps): JSX.Element {
  const t = useT();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  if (muted) return <span className={joined('talk__muted', className)}>{t('ui.talk.muted')}</span>;

  return (
    <div className={joined('talk', className)}>
      {open ? (
        <div className="talk__strip" role="group" aria-label={t('ui.talk.label')}>
          {TABLE_TALK.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className="talk__preset"
              onClick={() => {
                onSend(preset.id);
                setOpen(false);
              }}
            >
              {talkText(t, preset.id)}
            </button>
          ))}
        </div>
      ) : null}
      <button
        type="button"
        className="btn btn--ghost"
        disabled={disabled}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        {t('ui.talk.label')}
      </button>
    </div>
  );
}

export interface TableTalkFloatProps {
  /** Where a seat's bubble lands on the table; percentages of the layer. */
  anchor?: (seat: number) => CSSProperties;
  className?: string;
}

/** Animates recent presets onto the table. Reads the store; renders nothing else. */
export function TableTalkFloat({ anchor = seatAnchor, className }: TableTalkFloatProps): JSX.Element {
  const t = useT();
  const talk = useApp((s) => s.talk);
  const [, expire] = useReducer((n: number) => n + 1, 0);
  const now = Date.now();
  const live = talk.filter((t) => now - t.at < LIFETIME_MS);

  // Re-render exactly when the oldest live bubble is due to clear, then again
  // for the next one — no polling while the table is quiet.
  useEffect(() => {
    const oldest = live[0];
    if (oldest === undefined) return;
    const id = setTimeout(expire, Math.max(20, LIFETIME_MS - (Date.now() - oldest.at) + 20));
    return () => clearTimeout(id);
  });

  return (
    <div className={joined('talk-float', className)} aria-live="polite">
      {live.map((entry) => (
        <span className="talk-float__bubble" key={`${entry.seat}-${entry.at}`} style={anchor(entry.seat)}>
          {talkText(t, entry.preset)}
        </span>
      ))}
    </div>
  );
}

/** Four seats, four table edges — bottom is always the local player's side. */
function seatAnchor(seat: number): CSSProperties {
  const spots = [
    { left: '50%', top: '78%' },
    { left: '18%', top: '50%' },
    { left: '50%', top: '22%' },
    { left: '82%', top: '50%' },
  ] as const;
  return spots[seat % spots.length] ?? spots[0];
}

function joined(base: string, extra: string | undefined): string {
  return extra === undefined ? base : `${base} ${extra}`;
}
